import { apiUtils } from "../utils/apiUtils";

class ScanService {
	async createScan(scanData) {
		// Validate platform
		if (!scanData.platform) {
			throw new Error("Platform is required");
		}

		const response = await apiUtils.post("/api/scans", {
			...scanData,
			// Ensure required fields
			clientId: scanData.clientId,
			accountId: scanData.accountId,
			platform: scanData.platform,
			startDate: scanData.startDate,
			endDate: scanData.endDate,
			customKeywords: scanData.customKeywords || [],
			sensitivityLevel: scanData.sensitivityLevel || "medium",
			enabledCategories: scanData.enabledCategories || [
				"hate_speech",
				"harassment",
				"explicit",
				"violence",
				"political",
				"cultural",
				"misconduct",
				"drug_use",
				"self_harm",
				"spam",
				"other",
			],
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.error || "Failed to create scan");
		}

		const result = await response.json();

		// Return just the data part of the result
		return result.data;
	}

	async getScanStatus(scanId) {
		const response = await apiUtils.get(`/api/scans/${scanId}`);

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.error || "Failed to get scan status");
		}

		return response.json();
	}

	/**
	 * Mirror of the backend's window math (PlatformWindowGenerator: a scan is
	 * split into at most 4 contiguous windows, one per day for short ranges).
	 * Shown before the user commits, so the cost of a scan is never a surprise:
	 * windows drive scraper work, and on API-backed platforms (TikTok) each
	 * window spends paid vendor credits.
	 */
	estimateScanWindows(dateRange) {
		const start = new Date(dateRange.startDate);
		const end = new Date(dateRange.endDate);
		if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) return null;
		const days = Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)));
		return Math.max(1, Math.min(4, days));
	}

	/** Platforms whose scans consume metered third-party API credits. */
	usesPaidCredits(platform) {
		return String(platform || '').toLowerCase() === 'tiktok';
	}

	/**
	 * THE canonical platform list. Everything else derives from this.
	 *
	 * `disabled: true` means there is no scraper adapter, so an account on that
	 * platform can be created but will never produce a scan. Six components used
	 * to hardcode their own lists and all six disagreed - users could add
	 * Instagram/Facebook/LinkedIn accounts that silently never scanned, and could
	 * NOT add Reddit, which works. Deriving from one place removes that class of
	 * bug rather than fixing today's instance of it.
	 *
	 * `short` is the display-cased value stored on Account.platform (existing rows
	 * hold "X"). `value` is the lowercase form used by scans and filters.
	 * Keeping both avoids a data migration.
	 *
	 * Live set must match scraper-service AdapterRegistry: x, reddit, tiktok.
	 */
	getSupportedPlatforms() {
		return [
			{ value: "x", short: "X", label: "X (Twitter)", icon: "𝕏" },
			{ value: "tiktok", short: "TikTok", label: "TikTok", icon: "🎵" },
			{ value: "reddit", short: "Reddit", label: "Reddit", icon: "🔴" },
			{ value: "instagram", short: "Instagram", label: "Instagram", icon: "📷", disabled: true },
			{ value: "linkedin", short: "LinkedIn", label: "LinkedIn", icon: "💼", disabled: true },
			{ value: "facebook", short: "Facebook", label: "Facebook", icon: "👍", disabled: true },
			{ value: "youtube", short: "YouTube", label: "YouTube", icon: "▶️", disabled: true },
		];
	}

	/** Only platforms that can actually be scanned - what pickers should offer. */
	getEnabledPlatforms() {
		return this.getSupportedPlatforms().filter((p) => !p.disabled);
	}

	getPlatformWindowStrategy(platform) {
		const strategies = {
			x: "daily",
			reddit: "single",
			instagram: "weekly",
			tiktok: "single",
			linkedin: "weekly",
			facebook: "weekly",
			youtube: "single",
		};
		return strategies[platform] || "single";
	}

	estimateScanDuration(platform, dateRange) {
		const { startDate, endDate } = dateRange;
		const start = new Date(startDate);
		const end = new Date(endDate);
		const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

		const platformFactors = {
			x: 3, // 3 seconds per day
			reddit: 2, // 2 seconds per day
			instagram: 5, // 5 seconds per day
			tiktok: 2,
			linkedin: 4,
			facebook: 4,
			youtube: 3,
		};

		const baseDuration = (platformFactors[platform] || 3) * daysDiff;
		const analysisDuration = 30; // 30 seconds for AI analysis

		return baseDuration + analysisDuration;
	}

	formatProgress(progress) {
		// Handle both old flat structure and new nested structure
		const isNewStructure = progress.scraping || progress.analysis;

		if (isNewStructure) {
			// New nested structure from enhanced WebSocket events
			return {
				overall: progress.overallProgress || 0,
				scraping: {
					progress: progress.scraping?.progress || 0,
					currentWindow: progress.scraping?.windowsCompleted + 1 || 0,
					totalWindows: progress.scraping?.windowsTotal || 0,
					postsFound: progress.scraping?.postsFound || 0,
				},
				analysis: {
					progress: progress.analysis?.progress || 0,
					postsAnalyzed: progress.analysis?.postsAnalyzed || 0,
					flaggedPosts: progress.analysis?.flaggedPosts || 0,
				},
				lastUpdate: progress.timestamp || progress.lastUpdate,
				currentPhase: progress.currentPhase || 'scraping',
			};
		} else {
			// Legacy flat structure for backward compatibility
			return {
				overall: progress.overallProgress || 0,
				scraping: {
					progress: progress.scrapingProgress || 0,
					currentWindow: progress.currentWindow || 0,
					totalWindows: progress.totalWindows || 0,
					postsFound: progress.postsFound || 0,
				},
				analysis: {
					progress: progress.analysisProgress || 0,
					postsAnalyzed: progress.postsAnalyzed || 0,
					flaggedPosts: progress.flaggedPosts || 0,
				},
				lastUpdate: progress.lastUpdate,
				currentPhase: 'scraping',
			};
		}
	}

	formatResults(results) {
		return {
			totalPosts: results.totalPosts || 0,
			analyzedPosts: results.analyzedPosts || 0,
			flaggedPosts: results.flaggedPosts || 0,
			deletedPosts: results.deletedPosts || 0,
			riskBreakdown: results.riskBreakdown || {},
			processingTime: results.processingTime || 0,
			analysisTime: results.analysisTime || 0,
		};
	}

	getRiskCategories() {
		return [
			{ value: "hate_speech", label: "Hate Speech", color: "#DC2626" },
			{ value: "harassment", label: "Harassment", color: "#EA580C" },
			{ value: "explicit", label: "Explicit Content", color: "#D97706" },
			{ value: "violence", label: "Violence", color: "#B91C1C" },
			{ value: "political", label: "Political Content", color: "#7C3AED" },
			{ value: "cultural", label: "Cultural Issues", color: "#059669" },
			{ value: "misconduct", label: "Misconduct", color: "#DC2626" },
			{ value: "drug_use", label: "Drug Use", color: "#B45309" },
			{ value: "self_harm", label: "Self Harm", color: "#991B1B" },
			{ value: "spam", label: "Spam", color: "#6B7280" },
			{ value: "visual_content", label: "Visual Content", color: "#7C2D12" },
			{ value: "other", label: "Other", color: "#374151" },
		];
	}

	getSensitivityLevels() {
		return [
			{ value: "low", label: "Low", description: "Only severe violations" },
			{ value: "medium", label: "Medium", description: "Standard monitoring" },
			{ value: "high", label: "High", description: "Strict monitoring" },
		];
	}

	validateScanRequest(scanData) {
		const errors = [];

		if (!scanData.clientId) {
			errors.push("Client selection is required");
		}

		if (!scanData.accountId) {
			errors.push("Account selection is required");
		}

		if (!scanData.platform) {
			errors.push("Platform is required");
		}

		if (!scanData.username) {
			errors.push("Username is required");
		}

		if (!scanData.startDate) {
			errors.push("Start date is required");
		}

		if (!scanData.endDate) {
			errors.push("End date is required");
		}

		if (scanData.startDate && scanData.endDate) {
			const start = new Date(scanData.startDate);
			const end = new Date(scanData.endDate);

			if (start >= end) {
				errors.push("End date must be after start date");
			}

			if (end > new Date()) {
				errors.push("End date cannot be in the future");
			}

		}

		if (scanData.customKeywords && scanData.customKeywords.length > 100) {
			errors.push("Maximum 100 custom keywords allowed");
		}

		return errors;
	}
}

const scanService = new ScanService();
export default scanService; 